import { create } from 'zustand';
import { UserModel } from '@/features/auth/types';
import { storage } from '@/utils/storage.ts';
import { getUserPermissions } from '@/features/auth/api/getPermissions.ts';

type User = UserModel | null;

type UserStore = {
  user: User;
  isLoggedIn: boolean;
  token?: string;
  refreshToken?: string;
  isLoadingPermissions: boolean;
  setUpUserData: (user: User) => void;
  removeUserData: () => void;
  setToken: (token: string) => void;
  setRefreshToken: (refreshToken: string) => void;
  removeToken: () => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  updatePermissions: (permissions: string[]) => void;
  fetchPermissions: () => Promise<void>;
};

const getInitialStore = () => ({
  user: storage.getUser(),
  isLoggedIn: storage.getToken() !== null,
  token: storage.getToken(),
  refreshToken: storage.getRefreshToken(),
  isLoadingPermissions: false,
});

const useUserStore = create<UserStore>((set, get) => ({
  ...getInitialStore(),
  setUpUserData: (user: User) => {
    storage.setUser(user);
    set({ isLoggedIn: true, user });
  },
  removeUserData: () => {
    storage.removeUser();
    set({ isLoggedIn: false, user: null });
  },
  setToken: (token: string) => {
    storage.setToken(token);
    set({ token });
  },
  setRefreshToken: (refreshToken: string) => {
    storage.setRefreshToken(refreshToken);
    set({ refreshToken });
  },
  removeToken: () => {
    storage.removeToken();
    set({ token: undefined });
  },
  setIsLoggedIn: (isLoggedIn: boolean) => {
    set({ isLoggedIn });
  },
  logout: () => {
    storage.removeToken();
    storage.removeUser();
    storage.removeRefreshToken();
    window.location.href = '/login';
    set({ user: null, isLoggedIn: false, token: undefined, refreshToken: undefined });
  },
  hasPermission: (permission: string) => {
    const user = get().user;
    const permissions = user?.permissions || [];
    return permissions.length === 0 || permissions.includes(permission);
  },
  updatePermissions: (permissions: string[]) => {
    const user = get().user;
    if (user) {
      const updatedUser = { ...user, permissions };
      storage.setUser(updatedUser);
      set({ user: updatedUser });
    }
  },
  fetchPermissions: async () => {
    try {
      const response = await getUserPermissions();
      const permissions = response.permissions;
      
      const user = get().user;
      if (user) {
        const updatedUser = { ...user, permissions };
        storage.setUser(updatedUser);
        set({ user: updatedUser });
      }
    } catch (error) {
      console.error('Erro ao buscar permissões:', error);
    } finally {
      set({ isLoadingPermissions: false });
    }
  },
}));

export { useUserStore };